import RefundAccountFields, { useRefundAccount } from "./Component/RefundAccountFields";
import ContentState from "../../../Component/UI/ContentState";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import api from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import Layout_user from "../../../Component/UI/Layout_user";
import TopBar from "../../../Component/UI/TopBar";
import SearchAddrApi from "../Sign/Modal/SearchAddrApi";
import { useNicknameCheck } from "../../../Component/tool/useNicknameCheck";

interface UserInfo {
  name?: string | null;
  phone?: string | null;
  user_email?: string | null;
  nickname?: string | null;
  addr_num?: string | null;
  addr_basic?: string | null;
  addr_detail?: string | null;
}

const MyInfo = () => {
  const navigate = useNavigate();
  const location=useLocation();
  const refundSection=useRef<HTMLDivElement>(null);
  const refundAccount = useRefundAccount();
  const requestIdRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [postCode, setPostCode] = useState("");
  const [address, setAddress] = useState("");
  const [detailAddress, setDetailAddress] = useState("");
  const nicknameCheck = useNicknameCheck(nickname, userInfo?.nickname || "");

  const getErrorMessage = useCallback((err: any, fallback: string) => {
    const responseMessage = err?.response?.data?.message;
    if (Array.isArray(responseMessage)) return responseMessage[0] || fallback;
    if (typeof responseMessage === "string") return responseMessage;
    return fallback;
  }, []);

  const applyUserInfo = useCallback((data: UserInfo) => {
    setUserInfo(data);
    setName(data.name || "");
    setPhone(data.phone || "");
    setEmail(data.user_email || "");
    setNickname(data.nickname || "");
    setPostCode(data.addr_num || "");
    setAddress(data.addr_basic || "");
    setDetailAddress(data.addr_detail || "");
  }, []);

  const loadMyInfo = useCallback(async () => {
    const token = localStorage.getItem("key") || sessionStorage.getItem("key");
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await api.get(url.Communication_url.user_info);
      if (requestIdRef.current !== requestId) return;
      applyUserInfo(response.data || {});
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setUserInfo(null);
      setError(getErrorMessage(err, "내 정보를 불러오지 못했습니다."));
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [applyUserInfo, getErrorMessage, navigate]);

  useEffect(() => {
    loadMyInfo();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadMyInfo]);

  useEffect(()=>{
    if(loading||!refundAccount.ready||location.hash!=='#refund-account')return;
    const frame=requestAnimationFrame(()=>{refundSection.current?.scrollIntoView({block:'start'});refundSection.current?.querySelector<HTMLSelectElement>('select')?.focus({preventScroll:true});});
    return()=>cancelAnimationFrame(frame);
  },[loading,refundAccount.ready,location.hash]);

  const openAddressSearch = async () => {
    setError("");
    setMessage("");
    try {
      await SearchAddrApi({
        setFirAddr: setAddress,
        setPostCode,
        setDetail: setDetailAddress,
      });
    } catch (err) {
      setError(getErrorMessage(err, "주소 검색을 불러오지 못했습니다."));
    }
  };

  const saveMyInfo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    if (!nicknameCheck.available) { setError("닉네임 중복확인을 해주세요."); return; }
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const refund = refundAccount.payload();
      const response = await api.patch(url.Communication_url.update_profile, {
        ...(refund ? { refund_account: refund } : {}),
        nickname: nickname.trim(), name: name.trim(), phone: phone.replace(/[\s-]/g, ""), user_email: email.trim(),
        addr_num: postCode.trim(),
        addr_basic: address.trim(),
        addr_detail: detailAddress.trim(),
      });
      if (refund && (!Number.isSafeInteger(response.data?.refund_account?.version) || response.data.refund_account.version <= refund.expected_version)) throw new Error('환불 계좌 저장 결과를 확인하지 못했습니다. 다시 조회해주세요.');
      applyUserInfo(response.data || {});
      refundAccount.apply(response.data?.refund_account);
      setMessage("내 정보가 저장되었습니다.");
    } catch (err) {
      setError(getErrorMessage(err, err instanceof Error ? err.message : "내 정보를 저장하지 못했습니다."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout_user isScroll="none">
      <Page>
        <TopBar title="내 정보" />
        <Content aria-busy={loading}>
          {loading ? (
            <ContentState kind="loading" icon="account" title="내 정보를 불러오는 중입니다." />
          ) : error && !userInfo ? (
            <ContentState kind="error" icon="account" title={error} description="연결 상태를 확인하고 다시 불러와 주세요." action={{ label: "다시 불러오기", onClick: loadMyInfo }} />
          ) : (
            <ProfileForm id="my-info-profile-form" onSubmit={saveMyInfo}>
              <Section>
                <SectionTitle>기본 정보</SectionTitle>
                <FieldGroup>
                  <Label htmlFor="my-info-name">이름</Label>
                  <TextInput id="my-info-name" value={name} maxLength={10} autoComplete="name" onChange={event => setName(event.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="my-info-phone">연락처</Label>
                  <TextInput id="my-info-phone" type="tel" value={phone} maxLength={16} autoComplete="tel" onChange={event => setPhone(event.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="my-info-email">이메일</Label>
                  <TextInput id="my-info-email" type="email" value={email} maxLength={100} autoComplete="email" onChange={event => setEmail(event.target.value)} />
                </FieldGroup>
                <FieldGroup>
                  <Label htmlFor="my-info-nickname">닉네임</Label>
                  <AddressRow>
                  <TextInput
                    id="my-info-nickname"
                    value={nickname}
                    maxLength={10}
                    required
                    pattern=".*\S.*"
                    onChange={(event) => setNickname(event.target.value)}
                    autoComplete="nickname"
                    disabled={saving}
                  />
                  <AddressButton type="button" onClick={nicknameCheck.check} disabled={saving || nicknameCheck.checking || !nicknameCheck.valid}>
                    {nicknameCheck.checking ? "확인 중" : "중복확인"}
                  </AddressButton>
                  </AddressRow>
                  {nicknameCheck.message && <Notice $error={!nicknameCheck.available} role="status">{nicknameCheck.message}</Notice>}
                </FieldGroup>
              </Section>

              <Section>
                <SectionTitle>배송지 주소</SectionTitle>
                <FieldGroup>
                  <Label htmlFor="my-info-postcode">주소</Label>
                  <AddressRow>
                    <ReadOnlyInput id="my-info-postcode" value={postCode} readOnly placeholder="우편번호" />
                    <AddressButton type="button" onClick={openAddressSearch}>
                      주소 검색
                    </AddressButton>
                  </AddressRow>
                  <ReadOnlyInput value={address} readOnly placeholder="기본 주소" aria-label="기본 주소" />
                  <TextInput
                    value={detailAddress}
                    onChange={(event) => setDetailAddress(event.target.value)}
                    placeholder="상세 주소"
                    aria-label="상세 주소"
                    autoComplete="address-line2"
                  />
                </FieldGroup>
              </Section>

              <div id="refund-account-section" ref={refundSection} style={{scrollMarginTop:80}}><RefundAccountFields state={refundAccount} disabled={saving} /></div>
              {(error || message) && (
                <Notice $error={!!error} role={error ? "alert" : "status"}>
                  {error || message}
                </Notice>
              )}

            </ProfileForm>
          )}
        </Content>
        {!loading && userInfo && <SaveBar>
          <SaveButton type="submit" form="my-info-profile-form" disabled={saving || !nicknameCheck.available || !refundAccount.ready}>
            {saving ? "저장 중" : "저장"}
          </SaveButton>
        </SaveBar>}
      </Page>
    </Layout_user>
  );
};

export default MyInfo;

const Page = styled.main`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: var(--user-text);
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 24px 20px max(20px, env(safe-area-inset-bottom));
`;

const ProfileForm = styled.form`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: var(--user-text);
  font-size: 18px;
  font-family: var(--font-Pretendard-SemiBold);
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: var(--user-text-muted);
  font-size: 14px;
  font-family: var(--font-Pretendard-SemiBold);
`;

const TextInput = styled.input`
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  border: 1px solid var(--user-border);
  border-radius: 6px;
  padding: 0 14px;
  color: var(--user-text);
  font-size: 16px;
  font-family: var(--font-Pretendard-Regular);
  background: var(--user-surface-raised);

  &:focus-visible {
    border-color: var(--user-accent);
    outline: 2px solid rgba(0, 134, 111, 0.2);
  }
`;

const ReadOnlyInput = styled(TextInput)`
  color: var(--user-text-muted);
  background: var(--user-surface-muted);
  cursor: default;
`;

const AddressRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
`;

const BaseButton = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 6px;
  padding: 0 18px;
  cursor: pointer;
  font-size: 16px;
  font-family: var(--font-Pretendard-SemiBold);
  touch-action: manipulation;

  &:active:not(:disabled) {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 3px solid rgba(0, 134, 111, 0.35);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const AddressButton = styled(BaseButton)`
  min-width: 100px;
  border: 1px solid #6b6b6b;
  white-space: nowrap;
  color: var(--user-text);
  background: var(--user-surface-raised);
`;

const SaveButton = styled(BaseButton)`
  flex-shrink: 0;
  width: 100%;
  color: #ffffff;
  background: var(--user-button);
`;

const SaveBar = styled.div`
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 12px 20px max(20px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--user-border);
  background: var(--user-surface);
`;




const Notice = styled.div<{ $error: boolean }>`
  color: ${({ $error }) => ($error ? "var(--user-error)" : "var(--user-success)")};
  font-size: 14px;
  line-height: 1.5;
`;
